import { CalendarItem } from "../models/CalendarItem";

/** Formats a Date as YYYY-MM-DD using its local calendar parts. */
export const toYmd = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/** Parses YYYY-MM-DD into a local-calendar Date (no timezone shifting). */
export const parseYmd = (ymd: string): Date => {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/** First day of the calendar week — the instructor's preference. */
export type WeekStartDay = "Monday" | "Sunday";

/** The week-start day on or before the given date (Monday by default). */
export const startOfWeek = (date: Date, weekStart: WeekStartDay = "Monday"): Date => {
  const daysSinceStart =
    weekStart === "Sunday" ? date.getDay() : (date.getDay() + 6) % 7;
  return addDays(new Date(date.getFullYear(), date.getMonth(), date.getDate()), -daysSinceStart);
};

/** The 7 days of the week containing the given date, week-start first. */
export const getWeekDays = (anchor: Date, weekStart: WeekStartDay = "Monday"): Date[] => {
  const first = startOfWeek(anchor, weekStart);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
};

/**
 * The full month grid containing the given date: whole weeks (week-start first)
 * from the week of the 1st through the week of the last day of the month.
 */
export const getMonthGridDays = (anchor: Date, weekStart: WeekStartDay = "Monday"): Date[] => {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const gridStart = startOfWeek(firstOfMonth, weekStart);
  const gridEnd = addDays(startOfWeek(lastOfMonth, weekStart), 6);
  const days: Date[] = [];
  for (let day = gridStart; day <= gridEnd; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
};

/** Groups calendar items by their effective date (YYYY-MM-DD), preserving feed order. */
export const groupItemsByDate = (items: CalendarItem[]): Map<string, CalendarItem[]> => {
  const byDate = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const group = byDate.get(item.date);
    if (group) {
      group.push(item);
    } else {
      byDate.set(item.date, [item]);
    }
  }
  return byDate;
};

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
